import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

import { useUpdateStudyReportWithFindings } from '../../../../helpers/api/reports/reports';
import {
  ClinicalPrenatalForm,
  ClinicalPrenatalValues,
} from './ClinicalPrenatalForm';
import { toStudyDateRequestValue } from './date-utils';

export interface ClinicalPrenatalSectionProps {
  studyInstanceUid: string;
  studyDate?: string | null;
  readOnly?: boolean;
  initialValues?: Partial<ClinicalPrenatalValues> | null;
  onSubmitExisting?: (
    payload: Partial<ClinicalPrenatalValues>
  ) => void | Promise<void>;
  onUpdated?: (payload: Partial<ClinicalPrenatalValues>) => void;
  isSubmitting?: boolean;
  formVersion?: string | number | null;
}

function normalizeStudyReportPayload(payload: Partial<ClinicalPrenatalValues>) {
  return {
    ...payload,
    study_date: toStudyDateRequestValue(payload.study_date),
  };
}

export function ClinicalPrenatalSection({
  studyInstanceUid,
  studyDate,
  readOnly = false,
  initialValues,
  onSubmitExisting,
  onUpdated,
  isSubmitting = false,
  formVersion,
}: ClinicalPrenatalSectionProps) {
  const { t } = useTranslation('Patient');
  const { mutateAsync: updateStudyReport, isPending: isUpdatingStudyReport } =
    useUpdateStudyReportWithFindings(undefined, undefined, false);

  const effectiveFormVersion = useMemo(
    () => String(formVersion ?? studyInstanceUid),
    [formVersion, studyInstanceUid]
  );

  const handleSubmit = async (payload: Partial<ClinicalPrenatalValues>) => {
    if (readOnly) {
      return;
    }

    const updatePayload = normalizeStudyReportPayload(payload);

    try {
      if (onSubmitExisting) {
        await onSubmitExisting(updatePayload);
        onUpdated?.(payload);
        return;
      }

      await updateStudyReport({
        study_instance_uid: studyInstanceUid,
        ...updatePayload,
      });
      onUpdated?.(payload);
      message.success(t('prenatalExam.messages.updated'));
    } catch (error) {
      console.error('Failed to save prenatal exam form:', error);
      message.error(t('prenatalExam.messages.createError'));
    }
  };

  return (
    <ClinicalPrenatalForm
      initialValues={initialValues}
      formVersion={effectiveFormVersion}
      pregnancyMeta={{ studyDate }}
      mode="update"
      readOnly={readOnly}
      isSubmitting={Boolean(isSubmitting || isUpdatingStudyReport)}
      helperText={t('prenatalExam.section.helperWithStudy')}
      showIntroCard={false}
      showStudyDateField={false}
      onSubmit={handleSubmit}
    />
  );
}

export default ClinicalPrenatalSection;
